import React from 'react';
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form';

/**
 * パスワード入力フィールド用のカスタムフック
 * - 全角英数字・記号を半角に変換
 * - 空白文字を削除
 * - IME変換中は変換を抑制
 */
export function usePasswordInput<T extends FieldValues>({
  fieldName,
  setFormValue,
  onBlurCallback,
}: {
  fieldName: Path<T>;
  setFormValue: UseFormSetValue<T>;
  onBlurCallback?: (event: React.FocusEvent<HTMLInputElement>) => void;
}) {
  const [isComposing, setIsComposing] = React.useState(false);

  const normalizePassword = (value: string) =>
    value
      // 全角英数字を半角に変換
      .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) - 0xfee0)
      )
      // 全角記号を半角に変換
      .replace(/[！-～]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) - 0xfee0)
      )
      // 空白文字を削除
      .replace(/\s/g, '');

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const rawValue = event.currentTarget.value;
    if (isComposing) {
      setFormValue(fieldName, rawValue as PathValue<T, Path<T>>);
      return;
    }

    const newValue = normalizePassword(rawValue);
    setFormValue(fieldName, newValue as PathValue<T, Path<T>>);
  };

  const handleCompositionEnd = (
    event: React.CompositionEvent<HTMLInputElement>
  ) => {
    setIsComposing(false);
    const newValue = normalizePassword(event.currentTarget.value);
    setFormValue(fieldName, newValue as PathValue<T, Path<T>>);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlurCallback?.(event);
  };

  const handleFocus = () => {
    setIsComposing(false);
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  return {
    passwordInputProps: {
      name: fieldName,
      onFocus: handleFocus,
      onCompositionStart: handleCompositionStart,
      onCompositionEnd: handleCompositionEnd,
      onInput: handleInput,
      onBlur: handleBlur,
    },
  };
}
